
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    Grid,
    TextField,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { putJobOrder } from '../API/jobOrderAPI';
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import {
  normalizeJobTypeKeyToLabel,
  normalizeJobDescriptionKeyToLabel,
  normalizeDocumentKeyToLabel,
  normalizeEquipmentKeyToLabel,
} from "../JobOrders/utils/JobOrderConstants";

const theme = createTheme({
    palette: {
        primary: {
            main: "#1e5d45", // Custom primary color
        },
    },
});

const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    // margin: theme.spacing(4, 0),
    opacity: 0.8,
}));

const formatJobDateToDDMMMYYYY = (dateString) => {
    // Create a Date object from the "YYYY-MM-DD" string
    const dateObj = new Date(dateString);
    // Format the day with a leading zero if necessary
    const day = String(dateObj.getDate()).padStart(2, "0");
    // Define the abbreviated month names
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Get the month abbreviation
    const monthAbbrev = monthNames[dateObj.getMonth()];
    // Get the full year
    const year = dateObj.getFullYear();
    // Return the formatted date string
    return `${day}-${monthAbbrev}-${year}`;
};

const formatList = (arr, normalizer) =>
  Array.isArray(arr) ? arr.map(normalizer).join(", ") : "";


function EditEndorsementForm() {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const { group } = location.state || {};


    const [groupDisplay, setGroupDisplay] = useState(group)
    const [endorsementData, setEndorsementData] = useState({
        endorsement_form_driver: group?.jobOrders[0].endorsement_form_driver,
        endorsement_vehicle: group?.jobOrders[0].endorsement_vehicle || "",
        endorsement_dispatch_time: group?.jobOrders[0].endorsement_dispatch_time || ""
    });
    const [endorsementList, setEndorsementList] = useState([])

    const [endorsementDataList, setEndorsementDataList] = useState(
        group?.jobOrders?.map((jobOrder) => ({
            endorsement_form_id: jobOrder.endorsement_form_id,
            jobOrderID: jobOrder.id,
            landmark: jobOrder.landmark,
            price: jobOrder.price,
            required_chemical: jobOrder.required_chemical,
            special_notes: jobOrder.special_notes,
            number_of_visits_per_month: jobOrder.number_of_visits_per_month,
        })) || []
    );

    // Place these near the top of your component:
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteInput, setDeleteInput] = useState("");

    const navigate = useNavigate();



    const handleEndorsementFieldChange = (jobOrderID, field, value) => {
        setEndorsementDataList((prevList) =>
            prevList.map((item) =>
                item.jobOrderID === jobOrderID ? { ...item, [field]: value } : item
            )
        );
    };

    const handleDriverChange = (e) => {
        setEndorsementData((prev) => ({
            ...prev, // Spread the previous state correctly
            endorsement_form_driver: e.target.value, // Update the specific field
        }));
    };

    const handleVehicleChange = (e) => {
        setEndorsementData((prev) => ({
          ...prev,
          endorsement_vehicle: e.target.value,
        }));
      };
      
    const handleDispatchTimeChange = (e) => {
        setEndorsementData((prev) => ({
            ...prev,
            endorsement_dispatch_time: e.target.value,
        }));
    };      

    const convertToAmPm = (time24) => {
        const [hour, minute] = time24.split(':');
        const hourNum = parseInt(hour, 10);
        const suffix = hourNum >= 12 ? 'PM' : 'AM';
        const hour12 = hourNum % 12 || 12;
        return `${hour12}:${minute} ${suffix}`;
    };

    // console.log(group)
    useEffect(() => {
        if (group?.jobOrders) {
            const groupWithJobScope = {
                ...group,
                jobOrders: group.jobOrders.map((job) => {
                    const jobScope = job.competencies
                        ?.filter((competency) => competency !== "driving_2_wheels" && competency !== "driving_4_wheels")
                        .map((competency) => {
                            // Format the competency name
                            const formattedCompetency = competency
                                .replace(/_/g, ' ') // Replace underscores with spaces
                                .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word

                            // Determine specializationKey
                            const specializationKey = competency === "general_pest_control" ? "general_pest_control" : "termite";

                            // Format specialization words
                            const formattedSpecialization = (job.specialization?.[specializationKey] || []).map((word) =>
                                word.length === 2
                                    ? word.toUpperCase() // Capitalize 2-letter words (e.g., "QB")
                                    : word.charAt(0).toUpperCase() + word.slice(1) // Capitalize the first letter of other words
                            );

                            return {
                                competency: formattedCompetency,
                                specialization: formattedSpecialization,
                            };
                        }) || [];

                    return {
                        ...job,
                        job_scope: jobScope, // Add job_scope to each jobOrder
                    };
                }),
            };

            setGroupDisplay(groupWithJobScope); // Update the state with the modified group
        }
    }, [group]); // Trigger when the `group` prop changes

    const handleTechnicianChange = (e) => {
        setEndorsementData((prev) => ({
            ...prev, // Spread the previous state correctly
            endorsement_form_acct_tech: e.target.value, // Update the specific field
        }));
    };


    async function handleSubmit(e) {
        e.preventDefault();
        setIsLoading(true);

        console.log("Form submitted with:", endorsementDataList);


        try {
            await Promise.all(
                endorsementDataList.map(async (item) => {
                    const { jobOrderID, ...otherData } = item;
                    const payload = {
                        ...otherData,
                        endorsement_form_driver: endorsementData.endorsement_form_driver,
                        endorsement_vehicle: endorsementData.endorsement_vehicle,
                        endorsement_dispatch_time: endorsementData.endorsement_dispatch_time
                    };
                    await putJobOrder(jobOrderID, payload);
                    console.log(`Updated jobOrderID: ${jobOrderID}`);
                })
            );

            setSuccessMessage("Endorsement form submitted successfully.");
            setTimeout(() => {
                navigate("/manage-endorsement-form");
            }, 3000);
        } catch (error) {
            setErrorMessage(
                `An error occurred while submitting the Endorsement form: ${error.message}. Please check your network connection and try again.`
            );
        } finally {
            setIsLoading(false);
        }
    }

    async function handleDeleteConfirm() {
        if (deleteInput !== "DELETE") {
            setErrorMessage("Please type DELETE exactly to confirm deletion.");
            return;
        }
        setIsLoading(true);
        try {
            await Promise.all(
                endorsementDataList.map(async (item) => {
                    const { jobOrderID } = item;

                    // Only clear endorsement-related fields
                    const payload = {
                        endorsement_form_id: "",
                        endorsement_form_acct_tech: "",
                        endorsement_form_driver: "",
                        endorsement_vehicle: "",
                        endorsement_dispatch_time: "",
                    };

                    await putJobOrder(jobOrderID, payload);
                    console.log(`Cleared endorsement fields for jobOrderID: ${jobOrderID}`);
                })
            );

            setSuccessMessage("Endorsement form deleted successfully.");
            navigate("/manage-endorsement-form");
        } catch (error) {
            setErrorMessage(
                `An error occurred while deleting the endorsement form: ${error.message}. Please check your network connection and try again.`
            );
        } finally {
            setIsLoading(false);
            setIsDeleteDialogOpen(false);
            setDeleteInput("");
        }
    }

    return (
        <>
            <BaseFormWrapper
                titleIcon={<FactCheckIcon />}
                title="Operations"
                subTitle="Manage Endorsement Form"
                subTitleRoute="/manage-endorsement-form"
                formTitle="Edit Endorsement Form"
                successMessage={successMessage}
                setSuccessMessage={setSuccessMessage}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                isLoading={isLoading}
            >

                <form
                    onSubmit={handleSubmit}
                >
                    <Grid item xs={12}>
                        <Grid container spacing={3} sx={{ mb: 2 }}>
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="flex-end">
                                    <Grid item xs={2}>
                                        <TextField
                                            label="Endorsement Form ID"
                                            name="endorsementFormID"
                                            value={group.endorsement_form_id || ''} // Assuming endorsementFormID is a state or variable
                                            InputLabelProps={{ shrink: true }}
                                            disabled
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={2}>
                                        <TextField
                                            label="Date"
                                            name="job_date"
                                            type="date" // Set the type to date
                                            value={group.jobOrders[0]?.job_date || ''} // Use the value from group.jobOrders[0].job_date
                                            InputLabelProps={{ shrink: true }} // Ensure the label shrinks
                                            disabled
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item sx={{ ml: 'auto' }}>
                                        <Button variant="contained" color="error" onClick={() => setIsDeleteDialogOpen(true)}>
                                            Delete
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <StyledHr />

                        <Grid item xs={12}>
                            <Grid container spacing={1}>
                                <Grid
                                    item
                                    xs={12}
                                    md={12}

                                >
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={5}>
                                            <FormControl fullWidth>
                                                <InputLabel>Driver</InputLabel>
                                                <Select
                                                    value={endorsementData.endorsement_form_driver}
                                                    name="driver"
                                                    onChange={handleDriverChange}
                                                    label="Driver"
                                                >
                                                    {group.technicians.map((technician) => (
                                                        <MenuItem
                                                            key={technician.id}
                                                            value={`${technician.firstname} ${technician.lastname}`}
                                                        >
                                                            {technician.firstname} {technician.lastname}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={6} >
                                            <Typography variant="body1" paddingLeft={2}>
                                                Technicians: {group.technicians
                                                    .map(technician => `${technician.firstname} ${technician.lastname}`)
                                                    .join(', ')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {/* Vehicle and Dispatch Time inputs */}
                                    <Grid container spacing={1} style={{ marginTop: 10 }}>
                                        <Grid item xs={3}>
                                        <TextField
                                            label="Vehicle"
                                            name="vehicle"
                                            value={endorsementData.endorsement_vehicle}
                                            onChange={handleVehicleChange}
                                            fullWidth
                                        />
                                        </Grid>
                                        <Grid item xs={2}>
                                        <TextField
                                            label="Dispatch Time"
                                            name="dispatch_time"
                                            type="time" // Time picker
                                            value={endorsementData.endorsement_dispatch_time}
                                            onChange={handleDispatchTimeChange}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 300 }} // Optional: 5-minute increments
                                        />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={1} style={{ marginTop: 10 }}>
                                        <Grid item xs={12}>
                                            <TableContainer
                                                component={Paper}
                                                sx={{
                                                    boxShadow: 'none',
                                                    padding: 0,
                                                    margin: 0,
                                                    overflowX: 'auto',
                                                    position: 'relative'
                                                }}
                                            >
                                                <Table
                                                    size="small"
                                                    sx={{
                                                        // minWidth: 3000,
                                                        tableLayout: 'fixed',
                                                        '& .MuiTableCell-root': { fontSize: '0.8rem', padding: '4px' }
                                                    }}
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ width: '200px' }}>Endorser Name</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Endorser Contact Number</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Service Availed</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Job Description</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Client</TableCell>
                                                            <TableCell sx={{ width: '100px' }}>Date</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Preferred Time</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Address</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Contact Person</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Client Contact Details</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Status of Last Visit</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Required Documents to Bring</TableCell>
                                                            <TableCell sx={{ width: '100px' }}>Size in sqm</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Payment Method</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Equipment to Bring</TableCell>
                                                            <TableCell sx={{ width: '100px' }}>With Complaints</TableCell>
                                                            <TableCell sx={{ width: '100px' }}>Schedule Confirmed with Client</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Landmark</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Price</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Required Chemical</TableCell>
                                                            <TableCell sx={{ width: '300px' }}>Special Notes</TableCell>
                                                            <TableCell sx={{ width: '200px' }}>Number of Visits per Month</TableCell>

                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {group.jobOrders.map((jobOrder) => {
                                                            const endorsement =
                                                                endorsementDataList.find((item) => item.jobOrderID === jobOrder.id) ||
                                                                {};
                                                            return (
                                                                <TableRow key={jobOrder.id}>
                                                                    <TableCell>
                                                                        {jobOrder.endorser_name || ''}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {jobOrder.endorser_contact_number || ''}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {formatList(jobOrder.job_types, normalizeJobTypeKeyToLabel)}
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        {formatList(jobOrder.job_description, normalizeJobDescriptionKeyToLabel)}
                                                                    </TableCell>
                                                                    <TableCell>{jobOrder.client.name}</TableCell>
                                                                    <TableCell>
                                                                        {formatJobDateToDDMMMYYYY(jobOrder.job_date)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {convertToAmPm(jobOrder.start_time)} -{' '}
                                                                        {convertToAmPm(jobOrder.end_time)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {jobOrder.client.address.house_unit_street || "No street"},{' '}
                                                                        {jobOrder.client.address.barangay || "No barangay"},{' '}
                                                                        {jobOrder.client.address.city || "No city"}{' '}
                                                                        {jobOrder.client.address.metro_province || ''}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {jobOrder.client.contact.firstname}{' '}
                                                                        {jobOrder.client.contact.lastname}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {jobOrder.client.contact?.number?.type || ''}: {jobOrder.client.contact?.number?.number || ''}
                                                                        <br />
                                                                        {jobOrder.client.contact?.email?.email || ''}
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        {jobOrder.current_status ? (
                                                                            typeof jobOrder.current_status === "string" ? (
                                                                                jobOrder.current_status
                                                                                    .split("_")
                                                                                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                                                    .join(" ")
                                                                            ) : (
                                                                                jobOrder.current_status
                                                                            )
                                                                        ) : (
                                                                            ""
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {formatList(jobOrder.documents_to_bring, normalizeDocumentKeyToLabel)}
                                                                    </TableCell>
                                                                    <TableCell>{jobOrder.client.address.area}</TableCell>
                                                                    <TableCell>
                                                                        {Array.isArray(jobOrder.payment_method)
                                                                            ? jobOrder.payment_method.map((pm) => pm.label).join(", ")
                                                                            : jobOrder.payment_method || ""}
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        {formatList(jobOrder.equipment, normalizeEquipmentKeyToLabel)}
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        {jobOrder.client_complaints
                                                                            ? jobOrder.client_complaints.charAt(0).toUpperCase() +
                                                                            jobOrder.client_complaints.slice(1)
                                                                            : ""}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {jobOrder.schedule_confirmed
                                                                            ? jobOrder.schedule_confirmed.charAt(0).toUpperCase() +
                                                                            jobOrder.schedule_confirmed.slice(1)
                                                                            : ""}
                                                                    </TableCell>

                                                                    <TableCell>
                                                                        <TextField
                                                                            multiline
                                                                            minRows={3}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                endorsement.landmark !== undefined
                                                                                    ? endorsement.landmark
                                                                                    : jobOrder.landmark
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleEndorsementFieldChange(
                                                                                    jobOrder.id,
                                                                                    'landmark',
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            inputProps={{ style: { fontSize: '0.8rem' } }}
                                                                        />
                                                                    </TableCell>



                                                                    <TableCell>
                                                                        <TextField
                                                                            multiline
                                                                            minRows={3}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                endorsement.price !== undefined
                                                                                    ? endorsement.price
                                                                                    : jobOrder.price
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleEndorsementFieldChange(jobOrder.id, 'price', e.target.value)
                                                                            }
                                                                            inputProps={{ style: { fontSize: '0.8rem' } }}
                                                                        />
                                                                    </TableCell>


                                                                    <TableCell>
                                                                        <TextField
                                                                            multiline
                                                                            minRows={3}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                endorsement.required_chemical !== undefined
                                                                                    ? endorsement.required_chemical
                                                                                    : jobOrder.required_chemical
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleEndorsementFieldChange(
                                                                                    jobOrder.id,
                                                                                    'required_chemical',
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            inputProps={{ style: { fontSize: '0.8rem' } }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <TextField
                                                                            multiline
                                                                            minRows={3}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                endorsement.special_notes !== undefined
                                                                                    ? endorsement.special_notes
                                                                                    : jobOrder.special_notes
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleEndorsementFieldChange(
                                                                                    jobOrder.id,
                                                                                    'special_notes',
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            inputProps={{ style: { fontSize: '0.8rem' } }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <TextField
                                                                            multiline
                                                                            minRows={3}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            fullWidth
                                                                            value={
                                                                                endorsement.number_of_visits_per_month !== undefined
                                                                                    ? endorsement.number_of_visits_per_month
                                                                                    : jobOrder.number_of_visits_per_month
                                                                            }
                                                                            onChange={(e) =>
                                                                                handleEndorsementFieldChange(
                                                                                    jobOrder.id,
                                                                                    'number_of_visits_per_month',
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            inputProps={{ style: { fontSize: '0.8rem' } }}
                                                                        />
                                                                    </TableCell>

                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>

                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            display="flex"
                                            justifyContent="flex-end"
                                            alignItems="center"
                                            mt={2}
                                            mb={2}
                                            mr={2}
                                        >
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => navigate("/manage-endorsement-form")}
                                                sx={{
                                                    fontSize: "0.875rem",
                                                    color: "#FF4C4C",
                                                    borderColor: "#FF4C4C",
                                                }}
                                                disabled={isLoading}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                type="submit"
                                                sx={{
                                                    marginLeft: "10px",
                                                    fontSize: "0.875rem",
                                                }}
                                                disabled={isLoading}
                                            >
                                                Submit
                                            </Button>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                </form>

            </BaseFormWrapper>
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        To confirm deletion of the endorsement form, please type <strong>DELETE</strong> below.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Type DELETE"
                        fullWidth
                        variant="outlined"
                        value={deleteInput}
                        onChange={(e) => setDeleteInput(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        disabled={deleteInput !== "DELETE"}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    );
}

export default EditEndorsementForm;